import { subDays, format, startOfDay, endOfDay } from 'date-fns';
import type { TypesenseSearchParams } from '~/components/shipment/SearchAndFilterShipmentsTypesense';

const DATE_FORMAT_NO_TIMEZONE = `yyyy-MM-dd'T'HH:mm`;

// Calculate default dates - past 7 days (one week) with start of day / end of day
const getDefaultDates = () => {
  const today = new Date();
  const sevenDaysAgo = subDays(today, 7);

  return {
    startDate: format(startOfDay(sevenDaysAgo), DATE_FORMAT_NO_TIMEZONE),
    endDate: format(endOfDay(today), DATE_FORMAT_NO_TIMEZONE),
  };
};

const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultDates();

export const DEFAULT_TYPESENSE_SEARCH_PARAMS: TypesenseSearchParams = {
  searchString: '',
  startDate: defaultStartDate,
  endDate: defaultEndDate,
  includeLTL: false,
  advancedFilters: {},
  facetFilters: {},
  page: 1,
  pageSize: 25,
  sortBy: 'created_date_time',
  sortDirection: 'desc',
};

// Filter values
export const FILTER_VALUES = {
  ALL: 'all',
} as const;

// Default values
export const DEFAULT_VALUES = {
  CURRENCY_CODE: 'USD',
  LINEAR_UNIT: 'in',
  WEIGHT_UNIT: 'lb',
  SEPARATOR: ', ',
  EMPTY_STRING: '',
  PAGE_SIZE: 25,
  DEFAULT_PAGE: 1,
  QUERY_ALL: '*',
  SORT_DIRECTION_DESC: 'desc',
  FILTER_OPERATOR: ' && ',
} as const;

// Fulfillment types
export const FULFILLMENT_TYPES = {
  RETURNS: 'returns',
  CUSTOMER: 'customer',
  AT_LARGE: 'at_large',
  HUNDREDWEIGHT: 'hundredweight',
  RESHIP: 'reship',
  UNKNOWN: 'unknown',
} as const;

// Label statuses
export const LABEL_STATUSES = {
  SUCCESS: 'success',
} as const;

// Shipment type mappings for filters
export const SHIPMENT_TYPE_FILTER_MAP = {
  labelCreatedOnly: 'carrier_label',
  carrierSelectionOnly: 'carrier_selection_only',
  shiptimeErrorOnly: 'error',
} as const;

// Typesense field names
export const TYPESENSE_FIELDS = {
  CREATED_DATE_TIME: 'created_date_time',
  ORIGIN_ID_FACET: 'origin_id_facet',
  TENANT_ID_FACET: 'tenant_id_facet',
  FULFILLMENT_CONTEXT_ID_FACET: 'fulfillment_context_id_facet',
  SHIPMENT_TYPE_FACET: 'shipment_type_facet',
  TEST_MODE_FACET: 'test_mode_facet',
  DESTINATION_COUNTRY_CODE_FACET: 'destination_country_code_facet',
  DESTINATION_STATE_FACET: 'destination_state_facet',
  LABEL_STATUS_FACET: 'label_status_facet',
  SHIPMENT_PROPERTIES_FACET: 'shipment_properties_facet',
  SHIPMENT_CLASSIFICATIONS_FACET: 'shipment_classifications_facet',
  CARRIER_SELECTION_STATUS_FACET: 'carrier_selection_status_facet',
  ORIGIN_COUNTRY_CODE_FACET: 'origin_country_code_facet',
  ORIGIN_POSTAL_CODE: 'origin_postal_code',
  ORIGIN_STATE_FACET: 'origin_state_facet',
  DESTINATION_POSTAL_CODE: 'destination_postal_code',
  WEIGHT_POUNDS: 'weight_pounds',
  SHIPMENT_TAGS: 'shipment_tags',
  SHIP_OPTION_FACET: 'ship_option_facet',
  CARRIER_SERVICE_METHOD_ID_FACET: 'carrier_service_method_id_facet',
  CARRIER_ID_FACET: 'carrier_id_facet',
} as const;

// Array of all facet fields for service layer
export const FACET_FIELDS = [
  TYPESENSE_FIELDS.TENANT_ID_FACET,
  TYPESENSE_FIELDS.ORIGIN_ID_FACET,
  TYPESENSE_FIELDS.FULFILLMENT_CONTEXT_ID_FACET,
  TYPESENSE_FIELDS.TEST_MODE_FACET,
  TYPESENSE_FIELDS.DESTINATION_COUNTRY_CODE_FACET,
  TYPESENSE_FIELDS.DESTINATION_STATE_FACET,
  TYPESENSE_FIELDS.LABEL_STATUS_FACET,
  TYPESENSE_FIELDS.SHIPMENT_PROPERTIES_FACET,
  TYPESENSE_FIELDS.SHIPMENT_CLASSIFICATIONS_FACET,
  TYPESENSE_FIELDS.SHIP_OPTION_FACET,
  TYPESENSE_FIELDS.CARRIER_SERVICE_METHOD_ID_FACET,
  TYPESENSE_FIELDS.CARRIER_ID_FACET,
  TYPESENSE_FIELDS.SHIPMENT_TYPE_FACET,
] as const;
